import React, { useContext } from 'react';
import { Modal, Box, Typography, Button, Divider, Table, TableRow, TableCell, TableContainer, TableBody, TableHead } from '@mui/material';
import { ThemeContext } from '../../context/ThemeContext';
import { useSelector } from 'react-redux';
import { Circle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSymbolPrices } from '../../context/WebSocketContext';
import BidValue from '../global/BidAsk/BidValue';
import High from '../global/BidAsk/High';
import AskValue from '../global/BidAsk/AskValue';
import Low from '../global/BidAsk/Low';
import SpreadValue from '../global/BidAsk/SpreadValue';

const SymbolDetailsModal = ({ open, closeModal }) => {
  const { darkMode } = useContext(ThemeContext);
  const selectedSymbol = useSelector((state) => state.symbol.selectedSymbol);
  const { t } = useTranslation()
  const symbols = useSymbolPrices();
  const symbolData = symbols[selectedSymbol?.symbolId] || {};
  const allSymbolParams = useSelector((state) => state.symbol.allSymbolParams);
  const symbolParameterData = allSymbolParams?.[selectedSymbol?.symbolId] || null;
  const { group = [], unit = [] } = useSelector((state) => state.groupUnit)
  const digit = symbolData?.digits
  const symbolGroup = group.find((g) => g.id === symbolData?.parentId) || null;
  const amountUnit = unit.find((u) => u.id === symbolData?.amountUnitId) || null;
  //  console.log(symbolParameterData);

  const TradeLevel = {
    TradeLevel_Full: 0,
    TradeLevel_Buy: 1,
    TradeLevel_Sell: 2, // only sell allowed
    TradeLevel_Close: 3, // only close allowed
    TradeLevel_Disabled: 4, // disabled
  };
  const CalcType = {
    CalcType_Forex: 0,
    CalcType_Forex_No_Leverage: 1,
    CalcType_CFD: 2,
    CalcType_CFD_Leverage: 3,
    CalcType_Futures: 4,
  };
  const getWeekdayName = (dayNumber) => {
    const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return weekdays[dayNumber] || "Invalid Day";
  };

  return (
    <Modal open={open} closeModal={closeModal} aria-labelledby="symbol-details-modal" fullWidth maxWidth="lg">
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: darkMode ? "var(--background-color)" : 'var(--white)', width: '45%', }}>
        <Box className="bg-primary" sx={{ padding: '8px', }}>
          <Typography id="symbol-details-modal" variant="body1" sx={{ color: darkMode ? "var(--white)" : "var(--white)" }} align="center" >
            {symbolData?.name || 'Symbol Details'}
          </Typography>
        </Box>
        <Box sx={{ padding: '16px', maxHeight: '70vh', overflow: 'auto', }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 16px 1fr', gap: '16px', mt: 2, mb: 4 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('symbolGroup')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolGroup?.name}</Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('trade')}</Typography>
              <Typography fontWeight={500} variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>
                {symbolData?.tradeType === TradeLevel.TradeLevel_Full ? ("Full"
                ) : symbolData?.tradeType === TradeLevel.TradeLevel_Buy ? (
                  "Buy"
                ) : symbolData?.tradeType === TradeLevel.TradeLevel_Sell ? (
                  "Sell"
                ) : symbolData?.tradeType === TradeLevel.TradeLevel_Close ? (
                  "Close"
                ) : symbolData?.tradeType === TradeLevel.TradeLevel_Disabled ? (
                  "Disabled"
                ) : (
                  0
                )}
              </Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('calculation')}</Typography>
              <Typography fontWeight={500} variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>
                {symbolData?.calculationType === CalcType.CalcType_Forex ? ("Forex"
                ) : symbolData?.calculationType === CalcType.CalcType_Forex_No_Leverage ? (
                  "Forex (No Leverage)"
                ) : symbolData?.calculationType === CalcType.CalcType_CFD ? (
                  "CFD"
                ) : symbolData?.calculationType === CalcType.CalcType_CFD_Leverage ? (
                  "CFD (Leverage)"
                ) : (
                  "Futures"
                )}
              </Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('amountUnit')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{amountUnit?.description || 'N/A'}</Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('minAmount')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.minimumAmountPerDeal}</Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('weeklyOpen')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>  {`${getWeekdayName(symbolData?.tradingTimes?.weeklyOpenDay)} ${symbolData?.tradingTimes?.weeklyOpenFrom.substring(3, 8) || "N/A"}`}</Typography>

            </Box>

            <Box sx={{}}></Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('spread')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}><SpreadValue symbolId={symbolData.id} /></Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('contractSize')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolData?.contractSize ?? 0}</Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('digit')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolData?.digits || 'N/A'} </Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('expiration')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolData?.tradingTimes?.expiryDate || 'Missing'}</Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t("maxAmount")}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.maximumAmountPerDeal}</Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t("weeklyClose")}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>  {`${getWeekdayName(symbolData?.tradingTimes?.weeklyCloseDay)} ${symbolData?.tradingTimes?.weeklyCloseTo.slice(0, 5) || "N/A"}`}</Typography>
            </Box>
          </Box>
          <Divider />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', mb: 4, mt: 2, bgcolor: darkMode ? '#1a2133' : 'var(--white)', borderRadius: 2, }}>
            <Box display='flex' gap={2} sx={{ padding: '5px', mb: 1 }}>
              <Box display='flex' alignItems='center' gap={1}><Circle sx={{ color: '#007bff', fontSize: '0.6rem' }} /><Typography sx={{ color: darkMode ? "var(--white)" : "var(--black)" }} variant='caption'>{t('quotes')}</Typography></Box>
              <Box display='flex' alignItems='center' gap={1}><Circle sx={{ color: '#4CAF50', fontSize: '0.6rem' }} /><Typography sx={{ color: darkMode ? "var(--white)" : "var(--black)" }} variant='caption'>{t('trade')}</Typography></Box>
            </Box>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 500, textAlign: 'left' }} aria-label="weekdays table">
                <TableHead sx={{ padding: 0 }}>
                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                      <TableCell key={index} sx={{ border: 'none', whiteSpace: "nowrap", fontWeight: 'bold', color: darkMode ? 'var(--white)' : 'var(--black)', p: 0 }}>
                        <Typography variant='body2' sx={{ marginLeft: '6px', mb: 1 }}>{t(day)}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((_, index) => {
                      const openDay = symbolData?.tradingTimes?.weeklyOpenDay ?? -1;
                      const closeDay = symbolData?.tradingTimes?.weeklyCloseDay ?? -1;
                      const isWithinRange = openDay <= closeDay
                        ? index >= openDay && index <= closeDay
                        : index >= openDay || index <= closeDay;

                      return (
                        <TableCell key={index} sx={{ p: 0, border: 'none' }}>
                          <div style={{ height: '8px', width: '93%', backgroundColor: '#007bff', borderRadius: '4px', margin: '0 auto', opacity: isWithinRange ? 1 : 0.2,}} />
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((_, index) => {
                      const openDay = symbolData?.tradingTimes?.weeklyOpenDay ?? -1;
                      const closeDay = symbolData?.tradingTimes?.weeklyCloseDay ?? -1;
                      const closeTo = symbolData?.tradingTimes?.weeklyCloseTo;
                      const formattedCloseTo = closeTo ? `00:00-${closeTo.slice(0, 5)}` : '00:00-00:00';

                      const isWithinRange = openDay <= closeDay
                        ? index >= openDay && index <= closeDay
                        : index >= openDay || index <= closeDay;

                      return (
                        <TableCell key={index} sx={{ border: 'none', padding: '5px', whiteSpace: "nowrap", color: darkMode ? 'var(--white)' : 'var(--black)', fontSize: '13px'}}>
                          {isWithinRange ? formattedCloseTo : '00:00-00:00'}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((_, index) => {
                      const openDay = symbolData?.tradingTimes?.weeklyOpenDay ?? -1;
                      const closeDay = symbolData?.tradingTimes?.weeklyCloseDay ?? -1;
                      const isWithinRange = openDay <= closeDay
                        ? index >= openDay && index <= closeDay
                        : index >= openDay || index <= closeDay;

                      return (
                        <TableCell key={index} sx={{ p: 0, border: 'none' }}>
                          <div style={{
                            height: '8px', width: '93%', backgroundColor: '#4CAF50',
                            borderRadius: '4px', margin: '0 auto', opacity: isWithinRange ? 1 : 0.2,
                          }} />
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((_, index) => {
                      const openDay = symbolData?.tradingTimes?.weeklyOpenDay ?? -1;
                      const closeDay = symbolData?.tradingTimes?.weeklyCloseDay ?? -1;
                      const openFrom = symbolData?.tradingTimes?.weeklyOpenFrom;
                      const formattedOpenFrom = openFrom ? `00:00-${openFrom.slice(0, 5)}` : '00:00-00:00';

                      const isWithinRange = openDay <= closeDay
                        ? index >= openDay && index <= closeDay
                        : index >= openDay || index <= closeDay;

                      return (
                        <TableCell key={index} sx={{ padding: '5px', whiteSpace: "nowrap", color: darkMode ? 'var(--white)' : 'var(--black)', fontSize: '13px'}}>
                          {isWithinRange ? formattedOpenFrom : '00:00-00:00'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

          </Box>

          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 16px 1fr', gap: '16px', mt: 2, mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('bid')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><BidValue symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('high')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><High symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t("open")}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}>  {symbolData?.open ? symbolData.open?.toFixed(digit) : "0.00"}</Typography>
            </Box>
            <Box sx={{}}></Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('ask')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><AskValue symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('low')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><Low symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: 'rgb(143, 162, 184)' }}>{t('close')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}>{symbolData?.close ? symbolData?.close.toFixed(digit) : "0.00"}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button onClick={closeModal} sx={{ m: 2, alignSelf: 'center' }} variant="outlined">
            {t('close')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SymbolDetailsModal;